import type { Extinguisher, Manufacturer, SparePart } from '@/data/types';

/** Price with VAT added. Catalog prices are stored excl. VAT (client spec). */
export function priceWithVat(price: number, vatPercent: number): number {
  return price * (1 + vatPercent / 100);
}

/**
 * Inverse of priceWithVat — the net (excl. VAT) price behind a gross one.
 *
 * Client #3 lets the user type EITHER side of the VAT pair in the service
 * price / spare part dialogs, but the net value stays what we persist, so a
 * gross entry has to be converted back. A vatPercent of -100 or lower would
 * divide by zero (or flip the sign); treat that as "no VAT" instead of
 * emitting Infinity into a price field.
 */
export function priceWithoutVat(gross: number, vatPercent: number): number {
  const factor = 1 + vatPercent / 100;
  if (factor <= 0) return gross;
  return gross / factor;
}

/** Money inputs are edited as plain decimals — 2 places, never localized. */
const PRICE_DECIMALS = 2;

/**
 * Format an amount for a `<input type="number">` price field. Non-finite
 * values (empty / half-typed input) format to '' so the paired field clears
 * rather than showing a stale number.
 */
export function formatPriceInput(value: number): string {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(PRICE_DECIMALS);
}

/**
 * Client #3: the price dialogs show excl. VAT and incl. VAT side by side and
 * recalculate one from the other on every keystroke.
 *
 * These two helpers return the value for the field the user is NOT editing.
 * The edited field always keeps its raw text — rounding it while the caret is
 * in it makes the pair fight each other (typing "1.005" would snap back to
 * "1.00" mid-word, and the round-trip net→gross→net would drift the value the
 * user actually entered). Net stays the source of truth on save.
 */
export function netToGrossInput(net: string, vatPercent: number): string {
  const value = parseFloat(net);
  if (!Number.isFinite(value)) return '';
  return formatPriceInput(priceWithVat(value, vatPercent));
}

export function grossToNetInput(gross: string, vatPercent: number): string {
  const value = parseFloat(gross);
  if (!Number.isFinite(value)) return '';
  return formatPriceInput(priceWithoutVat(value, vatPercent));
}

/**
 * Resolve an extinguisher's model id from its stored manufacturer + model
 * NAMES (extinguishers store names, not ids). Returns undefined when the
 * device has no manufacturer/model or it can't be matched in the catalog.
 */
export function resolveModelId(
  mfrName: string | undefined,
  modelName: string | undefined,
  manufacturers: Manufacturer[]
): string | undefined {
  if (!mfrName || !modelName) return undefined;
  const mfr = manufacturers.find(m => m.name === mfrName);
  return mfr?.models.find(md => md.name === modelName)?.id;
}

/**
 * Whether a spare part may be used on a given extinguisher.
 *
 * A part with no `models` mapping is compatible with everything (safe
 * default — nothing is hidden until a part is explicitly scoped). When the
 * part IS scoped, it matches only if the device's resolved model id is in the
 * list. A device whose model can't be resolved (missing/unknown) is never
 * hidden, so incomplete catalog data can't make a real part disappear.
 */
export function isPartCompatible(
  part: SparePart,
  ext: Pick<Extinguisher, 'mfr' | 'model'>,
  manufacturers: Manufacturer[]
): boolean {
  if (!part.models || part.models.length === 0) return true;
  const modelId = resolveModelId(ext.mfr, ext.model, manufacturers);
  if (!modelId) return true;
  return part.models.includes(modelId);
}
